import React, { useState } from 'react';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';
import { API_ENDPOINTS } from '../../../config/apiRouting';

interface LoyaltyConnectionData {
  loyaltyPlatform: string;
  loyaltyCredentials: {
    apiKey: string;
    integrationId: string;
  };
}

interface LoyaltyConnectionProps {
  data: LoyaltyConnectionData;
  onUpdate: (data: Partial<LoyaltyConnectionData>) => void;
  storeId: string;
}

type ConnectionStatus = 'idle' | 'testing' | 'success' | 'failure';

interface LoyaltyOption {
  id: string;
  name: string;
  icon: string;
  available: boolean;
  description: string;
  dataDescription: string;
}

const LOYALTY_OPTIONS: LoyaltyOption[] = [
  {
    id: 'punchh',
    name: 'Punchh',
    icon: 'fas fa-bullseye',
    available: true,
    description: 'Punchh loyalty and marketing platform',
    dataDescription: 'Customer loyalty data, reward redemptions, campaign performance, and guest profiles',
  },
  {
    id: 'thanx',
    name: 'Thanx',
    icon: 'fas fa-heart',
    available: true,
    description: 'Thanx guest engagement platform',
    dataDescription: 'Customer lifetime value, visit frequency, reward history, and retention metrics',
  },
  {
    id: 'paytronix',
    name: 'Paytronix',
    icon: 'fas fa-gift',
    available: true,
    description: 'Paytronix loyalty and gift card platform',
    dataDescription: 'Loyalty program analytics, gift card tracking, customer segmentation, and campaign ROI',
  },
];

export const LoyaltyConnection: React.FC<LoyaltyConnectionProps> = ({ data, onUpdate, storeId }) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
  const [connectionMessage, setConnectionMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handlePlatformSelect = (platformId: string) => {
    const option = LOYALTY_OPTIONS.find((o) => o.id === platformId);
    if (!option || !option.available) return;

    onUpdate({ loyaltyPlatform: platformId });
    setConnectionStatus('idle');
    setConnectionMessage('');
    setError(null);
  };

  const handleCredentialChange = (field: string, value: string) => {
    onUpdate({
      loyaltyCredentials: {
        ...data.loyaltyCredentials,
        [field]: value,
      },
    });
    if (connectionStatus !== 'idle') {
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  };

  const handleTestConnection = async () => {
    if (!data.loyaltyCredentials.apiKey) {
      setError('Please enter an API Key before testing.');
      return;
    }

    setConnectionStatus('testing');
    setConnectionMessage(`Testing connection to ${LOYALTY_OPTIONS.find(o => o.id === data.loyaltyPlatform)?.name}...`);
    setError(null);

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posConnectionTest(storeId), {
        method: 'POST',
        body: JSON.stringify({
          posType: data.loyaltyPlatform,
          credentials: {
            clientId: data.loyaltyCredentials.apiKey,
            clientSecret: data.loyaltyCredentials.integrationId,
          },
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setConnectionStatus('success');
        setConnectionMessage(result.message || 'Connection successful. Loyalty platform is linked.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setConnectionStatus('failure');
        setConnectionMessage(
          errorData.message || 'Connection failed. Please verify your credentials and try again.'
        );
      }
    } catch (err) {
      setConnectionStatus('failure');
      setConnectionMessage(
        err instanceof Error
          ? err.message
          : 'Unable to reach the server. Please check your network and try again.'
      );
    }
  };

  return (
    <div>
      <h2>Loyalty Platform</h2>
      <p className="wizard-step-description">
        Connect your loyalty platform to unlock guest engagement analytics, reward tracking,
        and AI-powered retention insights. This step is optional -- you can connect later from settings.
      </p>

      {error && (
        <div className="wizard-alert wizard-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className="pos-type-grid">
        {LOYALTY_OPTIONS.map((option) => (
          <div
            key={option.id}
            className={`pos-type-card ${data.loyaltyPlatform === option.id ? 'selected' : ''} ${
              !option.available ? 'disabled' : ''
            }`}
            onClick={() => handlePlatformSelect(option.id)}
          >
            {!option.available && <div className="pos-coming-soon-badge">Coming Soon</div>}
            <div className="pos-type-card-icon">
              <i className={option.icon}></i>
            </div>
            <div className="pos-type-card-name">{option.name}</div>
            <div className="pos-type-card-status">{option.description}</div>
          </div>
        ))}
      </div>

      {data.loyaltyPlatform && (
        <div className="pos-credential-form">
          <h3>{LOYALTY_OPTIONS.find(o => o.id === data.loyaltyPlatform)?.name} Credentials</h3>
          <p className="wizard-step-description">
            {LOYALTY_OPTIONS.find(o => o.id === data.loyaltyPlatform)?.dataDescription}
          </p>

          <div className="wizard-form-group">
            <label>
              API Key <span className="required">*</span>
            </label>
            <input
              type="text"
              value={data.loyaltyCredentials.apiKey}
              onChange={(e) => handleCredentialChange('apiKey', e.target.value)}
              placeholder={`Enter your ${LOYALTY_OPTIONS.find(o => o.id === data.loyaltyPlatform)?.name} API Key`}
            />
          </div>

          {data.loyaltyPlatform === 'paytronix' && (
            <div className="wizard-form-group">
              <label>Integration ID</label>
              <input
                type="text"
                value={data.loyaltyCredentials.integrationId}
                onChange={(e) => handleCredentialChange('integrationId', e.target.value)}
                placeholder="Enter your Paytronix Integration ID"
              />
            </div>
          )}

          <button
            className="test-connection-btn"
            onClick={handleTestConnection}
            disabled={connectionStatus === 'testing' || !data.loyaltyCredentials.apiKey}
          >
            {connectionStatus === 'testing' ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Testing Connection...
              </>
            ) : (
              <>
                <i className="fas fa-plug"></i>
                Test Connection
              </>
            )}
          </button>

          {connectionStatus !== 'idle' && connectionStatus !== 'testing' && (
            <div className={`connection-status ${connectionStatus}`}>
              <i
                className={`fas ${
                  connectionStatus === 'success' ? 'fa-check-circle' : 'fa-times-circle'
                }`}
              ></i>
              <span>{connectionMessage}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoyaltyConnection;
